import { useContext, useMemo, useSyncExternalStore } from "react";
import { ReactReduxContext } from "react-redux";
import { getAccess } from "./pageSecurity";
import { TemplateSecurityContext } from "./templateSecurityContext";

const noopSubscribe = () => () => {};
const noCurrentPage = () => undefined;

/*
 * Reads the active page's security straight from the store, so a component
 * deep inside a custom card can gate its Add / Edit / Delete controls without
 * the page having to drill `currentPage` down to it. Components already wired
 * to `currentPage` through connect() can pass it in to skip the subscription.
 *
 * Inside a view template the template's own security answers instead: it is
 * the block configured for that specific view, and it is already what A_Card
 * gates its own header buttons on. The page's security keeps answering for
 * chrome outside a card, and for a card whose template carries no block.
 *
 * The store is taken from ReactReduxContext rather than useSelector so that
 * atoms rendered outside a Provider (unit tests, Storybook) degrade to "no
 * rights" instead of throwing.
 */
const usePageSecurity = (currentPageOverride) => {
  const templateSecurity = useContext(TemplateSecurityContext);
  const reduxContext = useContext(ReactReduxContext);
  const store = reduxContext && reduxContext.store;

  const [subscribe, getCurrentPage] = useMemo(
    () =>
      store
        ? [store.subscribe, () => store.getState().currentPage]
        : [noopSubscribe, noCurrentPage],
    [store]
  );

  const currentPageFromStore = useSyncExternalStore(
    subscribe,
    getCurrentPage,
    getCurrentPage
  );
  const currentPage = currentPageOverride || currentPageFromStore;

  return useMemo(
    () =>
      templateSecurity
        ? getAccess(templateSecurity)
        : getAccess(
            currentPage &&
              currentPage.pageConfig &&
              currentPage.pageConfig.pageSecurity
          ),
    [currentPage, templateSecurity]
  );
};

export default usePageSecurity;
